import {
  BooleanInput,
  Edit,
  FormTab,
  Labeled,
  NumberInput,
  SaveButton,
  SelectInput,
  TabbedForm,
  Toolbar,
  minValue,
} from 'react-admin';
import Permission from '../components/Permission';
import { PERMISSIONS } from '../constants';
import { getCompanyId } from '../utils/utils';
import { AutoCompleteSelectArrayInput, Tooltip } from '../components';
import { RichTextInput } from 'ra-input-rich-text';
import { Divider, Grid } from '@mui/material';

const CompanyConfig = (props) => {

  const ToolbarListActions = () => (
    <Toolbar>
      <SaveButton />
    </Toolbar>
  );

  return (
    <Permission permission={PERMISSIONS.COMPANY_CONFIG}>
      <Edit
        id={getCompanyId()}
        title="Configurações"
        resource="company-config"
        mutationMode="pessimistic"
      >
        <TabbedForm toolbar={<ToolbarListActions />}>
          <FormTab label="Geral">
            <Grid container>
              <Grid item xs={12} md={6}>
                <Labeled label="Registro" fullWidth>
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Ao ativar esta opção, novos cadastros de veículos terão o campo 'Obrigar a informar o odômetro' marcado como ATIVO por padrão.">
                      <BooleanInput label="Exigir odômetro como padrão no registro de veículo" source={"vehicleOdometer"} fullWidth />
                    </Tooltip>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Ao ativar esta opção, novos cadastros de motoristas terão o campo 'Obrigar a informar o odômetro' marcado como ATIVO por padrão.">
                      <BooleanInput label="Exigir odômetro como padrão no registro de motorista" source={"driverOdometer"} fullWidth />
                    </Tooltip>
                  </>
                </Labeled>
                <Labeled label="Abastecimento" fullWidth>
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Quando ativada, permitirá que veículos abasteçam sem uma operação vinculada e, ao cadastrar um veículo, a operação será um campo opcional">
                      <BooleanInput label="Permitir abastecimentos e cadastro de veículos sem operação vinculada" source="isFillinWithoutOperation" fullWidth />
                    </Tooltip>
                    <Tooltip sxContainer={{ alignItems: 'center' }} placement="top-start" title="Tempo mínimo, em horas, que deve ser respeitado entre abastecimentos consecutivos.">
                      <NumberInput
                        label="Intervalo obrigatório entre abastecimentos (em horas)"
                        source={"intervalBetweenFillin"}
                        validate={minValue(0)}
                        fullWidth
                      />
                    </Tooltip>
                  </>
                </Labeled>
                <Labeled label="Contingência" fullWidth>
                  <>
                    <Tooltip sxContainer={{ alignItems: 'center' }} placement="top-start" title="Defina o número de contingências permitidas por mês para um motorista antes que a autenticação via OTP seja exigida. Após ultrapassar esse limite, o uso de OTP será obrigatório para autorizar novas contingências.">
                      <NumberInput
                        source="allowedContingencyLimit"
                        validate={minValue(0)}
                        label="Contingências liberadas"
                        fullWidth
                      />
                    </Tooltip>
                    <Tooltip
                      sxContainer={{ alignItems: 'center' }}
                      placement="top-start"
                      title={
                        <>
                          Define se o limite de contingências a ser considerado durante a validação será o do motorista ou da transportadora. <br /> Se escolher Motorista, será usado o limite configurado diretamente no cadastro dele. <br />Se escolher Transportadora, será considerado o valor definido no campo acima.
                        </>
                      }
                    >
                      <SelectInput
                        emptyText="ra.action.clear_input_value"
                        source="precedenceType"
                        choices={[
                          { id: 'company', name: 'Transportadora' },
                          { id: 'driver', name: 'Motorista' }
                        ]}
                        label="Precedência"
                        fullWidth
                      />
                    </Tooltip>
                  </>
                </Labeled>
              </Grid>
            </Grid>

          </FormTab>
          <FormTab label="Emails">
            <Grid container>
              <Grid item xs={12} md={6}>
                <Labeled label="Geral" fullWidth>
                  <Tooltip sxContainer={{ alignItems: 'center' }} placement="top-start" title="Escolha a(s) lista(s) de e-mails já cadastradas que receberão notificação quando um abastecimento for aprovado manualmente.">
                    <AutoCompleteSelectArrayInput
                      label="Disparar e-mail na aprovação manual do abastecimento (listas)"
                      source="approvedMailingListIds"
                      references="mailing-list"
                      fullWidth
                    />
                  </Tooltip>
                </Labeled>
                <Divider sx={{ width: "100%", marginBottom: 2, marginTop: 1 }} />
                <Labeled
                  label={
                    <Tooltip sxContainer={{ alignItems: 'center' }} placement="top-start" title="Personalize as notificações por e-mail para situações em que o abastecimento exige aprovação manual, especificando as listas de e-mails e o corpo da mensagem.">
                      {'Configuração de email para Transportadora'}
                    </Tooltip>
                  }
                  fullWidth
                >
                  <>
                    <Tooltip sxContainer={{ alignItems: 'center' }} placement="top-start" title="Selecione as listas de e-mails previamente cadastradas que deverão ser notificadas sempre que um abastecimento não for aprovado automaticamente e exigir análise manual.">
                      <AutoCompleteSelectArrayInput
                        label="Disparar e-mail quando não houver aprovação imediata  (listas)"
                        source="withoutApprovalMailingListIds"
                        references="mailing-list"
                        fullWidth
                      />
                    </Tooltip>
                    <RichTextInput label="Mensagem" source="companyBody" />
                  </>
                </Labeled>
                  <>
                    <Tooltip sxContainer={{ alignItems: 'center' }} placement="top-start" title="Selecione as listas de e-mails previamente cadastradas que deverão ser notificadas caso um abastecimento bloqueado automaticamente não seja revisado por mais de 1 hora.">
                      <AutoCompleteSelectArrayInput
                        label="Disparar e-mail quando não houver aprovação/reprovação imediata da transportadora (listas)"
                        source="blockedMailingListIds"
                        references="mailing-list"
                        fullWidth
                      />
                    </Tooltip>
                    <RichTextInput label="Mensagem" source="companyBlockedBody" />
                  </>
                <Divider sx={{ width: "100%", marginBottom: 2, marginTop: 2 }} />
                <Labeled
                  label={
                    <Tooltip sxContainer={{ alignItems: 'center' }} placement="top-start" title="Personalize as notificações por e-mail para situações em que o abastecimento exige aprovação manual, especificando as listas de e-mails e o corpo da mensagem.">
                      {'Configuração de email para Rede'}
                    </Tooltip>
                  }
                  fullWidth
                >
                  <>
                    <Tooltip sxContainer={{ alignItems: 'center' }} placement="top-start" title="Selecione as listas de e-mails de redes, previamente cadastradas, que deverão ser notificadas sempre que um abastecimento não for aprovado automaticamente e exigir análise manual.">
                      <AutoCompleteSelectArrayInput
                        label="Disparar e-mail para Redes quando não houver aprovação imediata (listas)"
                        source="chainMailingListIds"
                        references="mailing-list"
                        defaultFilter={{ isLinked: true }}
                        fullWidth
                      />
                    </Tooltip>
                    <RichTextInput label="Mensagem" source="chainBody" fullWidth />
                  </>
                </Labeled>
              </Grid>
            </Grid>
          </FormTab>
        </TabbedForm>
      </Edit>
    </Permission>
  );
};

const resource = {
  list: CompanyConfig,
  name: 'company-config',
  permissions: [PERMISSIONS.COMPANY_CONFIG]
};

export default resource;